import { useOne, useGo, useDelete, useNotification } from "@refinedev/core";
import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  FileText,
  Calendar,
  HardDrive,
  Hash,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  RefreshCw,
  File,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FormHeader } from "@/components/forms";
import { EntityAnalyticsCard, EntityAnalyticsGrid } from '@/components/shared';
import { type KnowledgeDocument, type DocumentJobStatus, FILE_TYPE_LABELS } from './types';

const STATUS_LABEL_MAP: Record<KnowledgeDocument['status'], string> = {
  PENDING: 'В очереди',
  PROCESSING: 'Обработка',
  COMPLETED: 'Готов',
  FAILED: 'Ошибка',
};

function getStatusIcon(status: KnowledgeDocument['status']) {
  switch (status) {
    case 'PENDING': return <Clock className="h-4 w-4" />;
    case 'PROCESSING': return <Loader2 className="h-4 w-4 animate-spin" />;
    case 'COMPLETED': return <CheckCircle className="h-4 w-4" />;
    case 'FAILED': return <XCircle className="h-4 w-4" />;
    default: return <Clock className="h-4 w-4" />;
  }
}

/**
 * Форматирование размера файла
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) {return `${bytes} B`;}
  if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`;}
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Получить иконку по типу файла
 */
function getFileIcon(mimeType: string) {
  if (mimeType === "application/pdf") {
    return <FileText className="h-8 w-8 text-red-500" />;
  }
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel") || mimeType === "text/csv") {
    return <FileText className="h-8 w-8 text-green-500" />;
  }
  if (mimeType.includes("wordprocessing")) {
    return <FileText className="h-8 w-8 text-blue-500" />;
  }
  return <File className="h-8 w-8 text-muted-foreground" />;
}

/**
 * Knowledge Document show/detail page
 */
export function KnowledgeDocumentsShow() {
  const go = useGo();
  const { id } = useParams<{ id: string }>();
  const { open: notify } = useNotification();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [jobStatus, setJobStatus] = useState<DocumentJobStatus["job"] | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);

  const docResult = useOne<KnowledgeDocument>({
    resource: "knowledge-documents",
    id: Number(id),
  });

  const doc = docResult.result;
  const isLoading = docResult.query?.isLoading ?? false;
  const refetch = docResult.query?.refetch;

  const { mutate: deleteDoc, mutation: deleteMutation } = useDelete();
  const isDeleting = deleteMutation.isPending;

  /**
   * Повторная обработка документа
   */
  const handleReprocess = async () => {
    if (!doc) {return;}

    setIsReprocessing(true);
    try {
      const response = await fetch(`/api/knowledge-base/${doc.id}/reprocess`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        notify?.({
          type: "success",
          message: "Документ добавлен в очередь на повторную обработку",
        });
        refetch?.();
      } else {
        const error = await response.json();
        notify?.({
          type: "error",
          message: error.error?.message || "Ошибка при повторной обработке",
        });
      }
    } catch {
      notify?.({
        type: "error",
        message: "Ошибка при повторной обработке документа",
      });
    } finally {
      setIsReprocessing(false);
    }
  };

  // Poll for status updates if document is processing
  useEffect(() => {
    if (!doc || (doc.status !== "PENDING" && doc.status !== "PROCESSING")) {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`/api/knowledge-base/${doc.id}/status`, {
          credentials: "include",
        });
        if (response.ok) {
          const result = await response.json();
          setJobStatus(result.data?.job || null);
          // Refetch document data if status changed
          if (result.data?.document?.status !== doc.status) {
            refetch?.();
          }
        }
      } catch {
        // Ignore polling errors
      }
    }, 3000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, doc?.status, refetch]);

  const handleDelete = () => {
    if (!doc) {return;}
    deleteDoc(
      { resource: "knowledge-documents", id: doc.id },
      {
        onSuccess: () => {
          go({ to: "/knowledge-documents" });
        },
      }
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: KnowledgeDocument["status"]) => {
    switch (status) {
      case "PENDING":
        return (
          <Badge variant="outline" className="text-yellow-600 border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20">
            <Clock className="h-3 w-3 mr-1" />
            В очереди
          </Badge>
        );
      case "PROCESSING":
        return (
          <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 dark:bg-blue-950/20">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Обработка
          </Badge>
        );
      case "COMPLETED":
        return (
          <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50 dark:bg-green-950/20">
            <CheckCircle className="h-3 w-3 mr-1" />
            Готов
          </Badge>
        );
      case "FAILED":
        return (
          <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50 dark:bg-red-950/20">
            <XCircle className="h-3 w-3 mr-1" />
            Ошибка
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Документ не найден</p>
        <Button
          variant="link"
          onClick={() => go({ to: "/knowledge-documents" })}
        >
          Вернуться к списку
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FormHeader
        title={doc.filename}
        description={
          <span className="inline-flex items-center gap-2">
            {FILE_TYPE_LABELS[doc.mimeType] || doc.mimeType}
          </span>
        }
        titleAddon={getStatusBadge(doc.status)}
        onBack={() => go({ to: "/knowledge-documents" })}
        actions={
          <div className="flex items-center gap-2">
            {isPolling && (
              <Button variant="outline" size="icon" onClick={() => refetch?.()} title="Обновить">
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
            {(doc.status === "FAILED" || doc.status === "COMPLETED") && (
              <Button
                variant="outline"
                onClick={handleReprocess}
                disabled={isReprocessing}
              >
                {isReprocessing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2" />
                )}
                Повторить обработку
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Удалить
            </Button>
          </div>
        }
        icon={getFileIcon(doc.mimeType)}
        sticky
      />

      <EntityAnalyticsGrid>
        <EntityAnalyticsCard
          title="Статус"
          value={STATUS_LABEL_MAP[doc.status] ?? doc.status}
          icon={getStatusIcon(doc.status)}
        />
        <EntityAnalyticsCard
          title="Размер файла"
          value={formatFileSize(doc.fileSize)}
          icon={<HardDrive className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Количество чанков"
          value={doc.status === 'COMPLETED' ? doc.chunksCount : '—'}
          icon={<Hash className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Загружен"
          value={formatDate(doc.createdAt)}
          icon={<Calendar className="h-4 w-4" />}
        />
      </EntityAnalyticsGrid>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Main info */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Информация о документе</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* File info */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <HardDrive className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Размер файла</p>
                  <p className="font-medium">{formatFileSize(doc.fileSize)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Количество чанков</p>
                  <p className="font-medium">
                    {doc.status === "COMPLETED" ? (
                      <span className="text-green-600">{doc.chunksCount}</span>
                    ) : (doc.status === "PROCESSING" ? (
                      <span className="text-blue-600">Обрабатывается...</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    ))}
                  </p>
                </div>
              </div>
            </div>

            {/* Error message */}
            {doc.status === "FAILED" && doc.error && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200 dark:bg-red-950/20 dark:border-red-900">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-red-700 dark:text-red-400">Ошибка обработки</p>
                    <p className="text-sm text-red-600 dark:text-red-300 mt-1">{doc.error}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={handleReprocess}
                      disabled={isReprocessing}
                    >
                      {isReprocessing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RotateCcw className="h-4 w-4 mr-2" />
                      )}
                      Повторить обработку
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Processing status */}
            {(doc.status === "PENDING" || doc.status === "PROCESSING") && (
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 dark:bg-blue-950/20 dark:border-blue-900">
                <div className="flex items-start gap-3">
                  <Loader2 className="h-5 w-5 text-blue-500 animate-spin mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-700 dark:text-blue-400">
                      {doc.status === "PENDING" ? "Ожидание в очереди" : "Обработка документа"}
                    </p>
                    <p className="text-sm text-blue-600 dark:text-blue-300 mt-1">
                      Документ автоматически разбивается на чанки и создаются векторные представления для поиска.
                    </p>
                    {jobStatus && (
                      <p className="text-xs text-blue-500 mt-2">
                        Статус задачи: {jobStatus.state} • Попыток: {jobStatus.attemptsMade}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Success message */}
            {doc.status === "COMPLETED" && (
              <div className="p-4 rounded-lg bg-green-50 border border-green-200 dark:bg-green-950/20 dark:border-green-900">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-700 dark:text-green-400">Документ готов</p>
                    <p className="text-sm text-green-600 dark:text-green-300 mt-1">
                      Документ успешно обработан. AI ассистент может использовать его для ответов на вопросы.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Временные метки</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Тип файла</p>
                  <p>{FILE_TYPE_LABELS[doc.mimeType] || doc.mimeType}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Загружен</p>
                  <p>{formatDate(doc.createdAt)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Обновлён</p>
                  <p>{formatDate(doc.updatedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить документ?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить документ «{doc.filename}»?
              Все связанные векторные данные также будут удалены. Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Удаление...
                </>
              ) : (
                "Удалить"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
